import { Fragment, useEffect, useRef, useState } from 'react'
import { useLocation } from 'wouter'
import Modal from '../components/ui/Modal'
import Button from '../components/ui/Button'

/**
 * Gestionnaire de modales IMPERATIF (S40 — ADR-066).
 *
 * `modales.ouvrir()` est appelable de partout — composant a n'importe quelle
 * profondeur, hook, voire code non-React (handler d'API). Plus aucun
 * `useState(showXxx)` a cabler ni de prop `onOpenXxx` a faire descendre :
 * chaque modale exporte son OUVREUR a cote de son composant (patron
 * `ouvrirFicheProducteur(...)` dans FicheProducteur.jsx).
 *
 * Regles :
 *  - une PILE : plusieurs modales peuvent se superposer, `id` deduplique ;
 *  - changer de route FERME les modales (une modale appartient a son ecran),
 *    sauf `persisteALaNavigation: true` ;
 *  - le contenu est FIGE a l'ouverture (snapshot des valeurs capturees) : y
 *    passer un payload (id, config), jamais une valeur vivante — le composant
 *    de contenu recharge lui-meme ses donnees (cache SWR) ;
 *  - le chrome visuel n'est PAS impose : les feuilles existantes gardent leur
 *    propre createPortal/style ; `confirmer()` s'appuie sur ui/Modal.
 *
 * Les ecrans a part entiere (RinaChatModal, ImmersiveScreen) ne passent PAS
 * par ici : ce sont des etats d'application, pas des modales.
 */

// --- Service singleton (initialise par le Provider) --------------------------
let _ouvrir = null
let _fermer = null
let compteur = 0
// Ouvertures demandees AVANT l'initialisation du Provider (les effets des
// enfants s'executent avant ceux du parent) : mises en attente, puis videes
// par le Provider a son montage.
let enAttente = []

export const modales = {
  /**
   * Ouvre une modale. `contenu` : ({ fermer }) => JSX.
   * Rend l'id (fourni ou genere) — idempotent sur un id deja ouvert.
   */
  ouvrir({ id, contenu, persisteALaNavigation = false }) {
    const idFinal = id ?? `modale-${++compteur}`
    const entree = { id: idFinal, contenu, persisteALaNavigation }
    if (_ouvrir) _ouvrir(entree)
    else enAttente.push(entree)
    return idFinal
  },

  fermer(id) {
    _fermer?.(id)
  },

  /**
   * Confirmation bloquante — rend une Promise<boolean>.
   *   const ok = await modales.confirmer({ titre: 'Retirer ce produit ?' })
   */
  confirmer({ titre, message, labelConfirmer = 'Confirmer', labelAnnuler = 'Annuler', danger = false }) {
    return new Promise((resolve) => {
      this.ouvrir({
        contenu: ({ fermer }) => {
          const choisir = (ok) => { fermer(); resolve(ok) }
          return (
            <Modal open onClose={() => choisir(false)} title={titre}>
              {message && <p className="text-sm text-ink-soft mb-4">{message}</p>}
              <div className="flex gap-2">
                <Button variant="ghost" fullWidth onClick={() => choisir(false)}>{labelAnnuler}</Button>
                <Button variant={danger ? 'danger' : 'primary'} fullWidth autoFocus onClick={() => choisir(true)}>{labelConfirmer}</Button>
              </div>
            </Modal>
          )
        },
      })
    })
  },
}

// --- Provider ----------------------------------------------------------------
export function ModalesProvider({ children }) {
  const [pile, setPile] = useState([])
  const [path] = useLocation()

  useEffect(() => {
    _ouvrir = (m) => setPile((p) => (p.find((x) => x.id === m.id) ? p : [...p, m]))
    _fermer = (id) => setPile((p) => p.filter((m) => m.id !== id))
    enAttente.splice(0).forEach((m) => _ouvrir(m))
    return () => { _ouvrir = null; _fermer = null }
  }, [])

  // Changer de route ferme la pile (sauf modales marquees persistantes).
  const routeInitiale = useRef(true)
  useEffect(() => {
    if (routeInitiale.current) { routeInitiale.current = false; return }
    setPile((p) => p.filter((m) => m.persisteALaNavigation))
  }, [path])

  return (
    <>
      {children}
      {pile.map((m) => (
        <Fragment key={m.id}>
          {m.contenu({ fermer: () => modales.fermer(m.id) })}
        </Fragment>
      ))}
    </>
  )
}
